import React, { useState, useEffect } from 'react';
import { Spin } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { SearchResultListProps } from '../types/bot.types';
import FlightCard2 from '../../../modules/SearchDatePicker/widgets/SearchResults/FlightCard2';
import { UrlPaxCount } from '../../../hooks/useCoreSearchParams';
import { TenantTsType } from '@skygroup/shared/constants/tenantTs';
import { getTsTypeByIdentifier } from '@skygroup/shared/utils/flightIdentifier';
import { SkyInventoryBadge } from '@skygroup/shared/components/sky-inventory-badge/SkyInventoryBadge';
import HotelResultCard from './HotelResultCard';
import PackageResultCard from './PackageResultCard';
import AttractionResultCard from './AttractionResultCard';
import EventResultCard from './EventResultCard';
import CollapsibleResultGroup from './CollapsibleResultGroup';

/** Leading emoji per product type for the collapsed-group header. */
const PRODUCT_ICON: Record<SearchResultListProps['productType'], string> = {
  flight: '✈️',
  hotel: '🏨',
  package: '🧳',
  activity: '🎢',
  event: '🎫',
};

const SearchResultList: React.FC<SearchResultListProps> = ({
  productType,
  results,
  items,
  onSelect,
  disabled = false,
  selectedKey,
  pickLabel,
}) => {
  // Track which card the user clicked so we can show an inline spinner on it
  // while add_to_cart runs. Once a new `results` array arrives (next search)
  // the state resets. The card list itself unmounts when its parent message
  // is no longer the last one — local state goes with it, which is fine.
  const [selectingIndex, setSelectingIndex] = useState<number | null>(null);
  useEffect(() => {
    setSelectingIndex(null);
  }, [results]);

  const t = useSelector((s: RootState) => (s.config.translations as any)?.bot);
  const { shoppingCartTranslations: tCart } = useSelector(
    (s: RootState) => s.config.translations
  );
  const addingLabel = t?.addingToCart ?? 'Adding to cart…';

  // Sky (own) inventory check — per product. Hotels may carry a top-level
  // `ts_type`; otherwise the base64 identifier embeds `ts` (same source
  // FlightCard2 / HotelList use). Flights don't need this here: FlightCard2
  // decodes the identifier and renders the badge itself.
  const isSkyInventoryHotel = (raw: any): boolean =>
    raw?.ts_type === TenantTsType.SKY ||
    getTsTypeByIdentifier({ identifier: raw?.identifier, type: 'hotel' }) ===
      TenantTsType.SKY;

  // The chat search payload doesn't carry the searched pax. If a raw flight item
  // ever includes it (backend may add adult/child/infant or a `pax` object),
  // use it for an accurate per-pax breakdown; otherwise the card falls back to
  // total-only (hidePaxBreakdown) so it never divides by a stale/default pax.
  const getPaxFromItem = (raw: any): UrlPaxCount | null => {
    const src = raw?.pax ?? raw;
    const hasPax =
      src?.adult != null || src?.child != null || src?.infant != null;
    if (!hasPax) return null;
    return {
      adult: Number(src.adult) || 0,
      child: Number(src.child) || 0,
      infant: Number(src.infant) || 0,
    };
  };

  if (!results?.length) return null;

  const handleSelect = (index: number, item: any) => {
    // Superseded list: view-only. Selection is dead (also guarded by CSS
    // pointer-events on the disabled group), so old options can't be re-picked.
    if (disabled) return;
    setSelectingIndex(index);
    const name = item.name ?? item.airline ?? item.hotelName ?? `Option ${index + 1}`;
    const summary = item.display_summary ?? `${productType} #${index + 1}`;
    onSelect(index, name, summary);
  };

  const renderCard = (item: any, i: number): React.ReactNode => {
    switch (productType) {
      case 'flight': {
              // Flights render through the existing FlightCard2 — pulls from
              // the raw provider object on `items[i]` (sideband). Falls back
              // to the summary item if items are absent (legacy sessions).
              // `isCompact` keeps the airline column + price + Select button;
              // `isMinimized` would hide both. `isPreviewMode` omitted so the
              // FlightButton2 renders and fires handlePurchase → handleSelect.
              const rawFlight = items?.[i] ?? item;
              const itemPax = getPaxFromItem(rawFlight);
              return (
                <FlightCard2
                  key={i}
                  flightData={rawFlight}
                  isCompact
                  handlePurchase={() => handleSelect(i, rawFlight)}
                  paxFromUrlParams={itemPax ?? undefined}
                  hidePaxBreakdown={!itemPax}
                />
              );
            }
            case 'hotel': {
              // Raw provider object from the sideband — carries ts_type /
              // identifier the slim summary doesn't. Same wrapper + badge as
              // the search page (HotelList.tsx).
              const rawHotel = items?.[i] ?? item;
              const isSkyInventory = isSkyInventoryHotel(rawHotel);
              return (
                <div
                  key={i}
                  className={`bot-hotel-card${
                    isSkyInventory ? ' is-local-inventory-hotel' : ''
                  }`}
                >
                  {isSkyInventory && (
                    <SkyInventoryBadge label={tCart?.recommendedHotel} />
                  )}
                  <HotelResultCard index={i}
                    name={item.name ?? item.info?.name ?? ''}
                    stars={item.stars ?? item.info?.stars ?? 3}
                    destination={item.destination ?? item.info?.address}
                    checkIn={item.checkIn ?? item.check_in ?? ''}
                    checkOut={item.checkOut ?? item.check_out ?? ''}
                    nights={item.nights ?? 1}
                    mealPlan={item.mealPlan ?? item.meal_plan ?? item.meal}
                    pricePerNight={item.pricePerNight ?? item.price_per_night}
                    totalPrice={item.totalPrice ?? item.total_price ?? item.lowest_room_rate?.total_price ?? 0}
                    currency={item.currency ?? item.lowest_room_rate?.currency ?? 'USD'}
                    freeCancellation={item.freeCancellation ?? item.free_cancellation}
                    rating={item.rating}
                    imageUrl={item.imageUrl ?? item.images?.[0] ?? item.info?.images?.[0]}
                    onSelect={() => handleSelect(i, item)}
                  />
                </div>
              );
            }
            case 'package': {
              // Prefer the raw provider object on items[i] (richer than the
              // slim summary). Probes accept both the bot summary shape
              // (hotel_name / meal_plan / total_price snake_case) AND the raw
              // (hotel.name / hotel.boardBasis / min_price + currency_sign +
              // dates.start/end + flights.flightFrom[0].airline.name).
              const raw = items?.[i] ?? item;
              const airlineRaw =
                raw.flights?.flightFrom?.[0]?.airline ?? raw.airline ?? null;
              const airlineLabel =
                typeof airlineRaw === 'string'
                  ? airlineRaw
                  : airlineRaw?.name ?? airlineRaw?.id ?? null;
              return (
                <PackageResultCard key={i} index={i}
                  hotelName={raw.hotel_name ?? raw.hotel?.name ?? raw.hotelName ?? raw.name ?? ''}
                  hotelStars={raw.hotel_stars ?? raw.hotel?.stars ?? raw.hotelStars ?? 3}
                  mealPlan={raw.meal_plan ?? raw.hotel?.boardBasis ?? raw.mealPlan}
                  nights={raw.nights}
                  airline={airlineLabel}
                  departure={raw.departure ?? raw.dates?.start ?? raw.departure_date ?? ''}
                  returnDate={raw.return ?? raw.dates?.end ?? raw.return_date ?? raw.returnDate}
                  totalPrice={raw.total_price ?? raw.totalPrice ?? raw.min_price ?? 0}
                  currency={raw.currency ?? raw.currency_sign ?? 'USD'}
                  imageUrl={raw.image ?? raw.imageUrl}
                  transferIncluded={!!(raw.is_include_transfer ?? raw.transfer_included)}
                  flights={raw.flights}
                  onSelect={() => handleSelect(i, raw)}
                />
              );
            }
            case 'activity': {
              // Prefer raw provider object for richer fields; probe both
              // bot summary (snake_case + `name`/`image`) AND raw provider
              // shape (`title`, `cityName`, `minPrice`, `priceCurrency`,
              // `galleryImages[].url`).
              const raw = items?.[i] ?? item;
              const heroImage =
                raw.image ??
                raw.imageUrl ??
                raw.galleryImages?.[0]?.url ??
                (typeof raw.thumbnailImage === 'string' && /^https?:/.test(raw.thumbnailImage)
                  ? raw.thumbnailImage
                  : undefined);
              return (
                <AttractionResultCard key={i} index={i}
                  name={raw.name ?? raw.title ?? ''}
                  category={raw.category ?? raw.categories?.[0]?.code}
                  location={raw.location ?? raw.cityName ?? raw.address}
                  date={raw.date}
                  duration={raw.duration ?? (raw.durationHours ? `${raw.durationHours} hours` : undefined)}
                  price={raw.price ?? raw.minPrice ?? raw.min_price ?? 0}
                  currency={raw.currency ?? raw.priceCurrency ?? 'USD'}
                  highlights={raw.highlights ?? raw.experienceHighlights ?? raw.description}
                  imageUrl={heroImage}
                  onSelect={() => handleSelect(i, raw)}
                />
              );
            }
            case 'event': {
              const raw = items?.[i] ?? item;
              return (
                <EventResultCard
                  key={raw.identifier ?? i}
                  event={raw}
                  onSelect={() => handleSelect(i, raw)}
                />
              );
            }
            default:
              return null;
          }
  };

  const list = (
    <div className="bot-search-results">
      <div
        className={`bot-search-results__list${
          productType === 'event' ? ' bot-search-results__list--events' : ''
        }`}
      >
        {/* No FE cap — the backend ResultFormatter owns list length (TOP_N),
            and the LLM's numbered text list must match the card stack 1:1. */}
        {results.map((item: any, i: number) => {
          const isSelecting = selectingIndex === i;
          const isPicked = disabled && selectedKey === i;
          return (
            <div
              key={i}
              className={`bot-result-card-wrapper${
                isSelecting ? ' bot-result-card-wrapper--selecting' : ''
              }${isPicked ? ' bot-result-card-wrapper--picked' : ''}`}
            >
              {isPicked && (
                <span className="bot-result-card-wrapper__pick-badge">
                  {t?.collapsed?.yourPick ?? 'Your pick'}
                </span>
              )}
              {renderCard(item, i)}
              {isSelecting && (
                <div className="bot-result-card-wrapper__overlay" role="status" aria-live="polite">
                  <Spin size="small" />
                  <span className="bot-result-card-wrapper__overlay-label">
                    {addingLabel}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  if (!disabled) return list;

  // Superseded: collapse the list into a view-only group the user can expand
  // to preview (cards stay disabled). The picked option is named in the header.
  return (
    <CollapsibleResultGroup
      icon={PRODUCT_ICON[productType] ?? '🧭'}
      title={t?.collapsed?.title?.[productType] ?? t?.collapsed?.title?.flight ?? 'Options'}
      count={results.length}
      pickLabel={pickLabel}
    >
      {list}
    </CollapsibleResultGroup>
  );
};

export default SearchResultList;
